import _ from 'lodash';
import { useDispatch } from 'react-redux';
import type { AnyAction } from 'redux';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import Notification from '@Common/Notification';
import './AddReviewTasks.scss';
import TutorialModal from './TutorialModal';
import React, { useState, useEffect, ReactElement, useRef } from 'react';
import CreateTaskForm from './Tasks/CreateTaskForm';
import * as types from '@Constants/actionTypes';
import { Task } from '../Interfaces/Types';
import { AddReviewTasksProps, FormFilterResponse } from '../Interfaces/PropTypes';
import ImportOptions from './ImportOptions';
import { ThunkDispatch } from 'redux-thunk';

const defaultFormData = {
	id: 0,
	name: '',
	description: '', //#TODO Check where to map
	contingentId: 0,
	phaseId: '',
	departmentId: '',
	communityId: '',
	jobTitleId: '',
	contingentName: '',
	contactName: '',
	contactEmail: '',
	additionalInfo: '',
	taskTagIds: [],
	isActive: true,
	isLocationTaskListActive: true,
	links: [],
	targetDaysOut: null,
	targetDays: null,
	targetWeeks: null,
	targetStaticDate: null,
	startDaysOut: null,
	startDays: null,
	startWeeks: null,
	startStaticDate: null,
	anticipatedEffort: '',
	steps: [],
	prereqTasks: [],
	showStaticDates: false, // only for ui
	createdByName: '', // Only display if Editing
	createdDate: '', // Only display if Editing
	modifiedByName: '', // Only display if Editing
	modifiedDate: '', // Only display if Editing
	isMilestone: false,
	isLocationTaskDateAdjustable: true,
	includeCertificationCheckbox: false,
	baselineIdsSelected: [],
};

const AddReviewTasks = ({ setWizardSubStep, setImportType, projectKey, subscriptionId, nextStep }: AddReviewTasksProps): ReactElement => {
	const [showTutorial, setShowTutorial] = useState<boolean>(false);
	const [showCreateTask, setShowCreateTask] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [showImportOptions, setShowImportOptions] = useState<boolean>(false);
	// #TODO discuss this
	const [baselineId, setBaselineId] = useState<number>(0);
	const [currentFormState, setCurrentFormState] = useState({
		editTask: null as Task | null,
		unchangedTask: null as Task | null,
		newTask: null as Task | null,
		isFormValid: true,
		showDateError: false,
		isLoading: false,
		taskSearchResults: [],
		taskPrereqSearchResults: [],
		unsavedChanges: false,
		saveAndNewRetention: false,
		baselineId: baselineId,
	});

	const [formData, setFormData] = useState<Task>({
		id: 0,
		name: '',
		description: '',
		contingentId: 0,
		phaseId: '',
		departmentId: '',
		communityId: '',
		jobTitleId: '',
		contingentName: '',
		baselineId: 0,
		contactName: '',
		contactEmail: '',
		additionalInfo: '',
		taskTagIds: [],
		isActive: true,
		isLocationTaskListActive: true,
		isLocationTaskDateAdjustable: true,
		links: [],
		targetDaysOut: null,
		targetDays: null,
		targetWeeks: null,
		targetStaticDate: null,
		startDaysOut: null,
		startDays: null,
		startWeeks: null,
		startStaticDate: null,
		anticipatedEffort: '',
		steps: [],
		prereqTasks: [],
		workflows: [],
		showStaticDates: false,
		isMilestone: false,
		createdByName: '',
		createdDate: '',
		modifiedByName: '',
		modifiedDate: '',
		includeCertificationCheckbox: false,
		baselineIdsSelected: [],
	});

	const [formFilters, setFormFilters] = useState<FormFilterResponse>({
		departments: [],
		subscriptionDepartments: [],
		baselineJobTitles: [],
		communities: [],
		phases: [],
		disciplines: [],
		certifiedTaskId: 0,
		subscriptionFiles: [],
		taskTags: [],
		isLoaded: false,
		isStartDateActive: false,
		isTaskStepsActive: false,
		isTaskEffortActive: false,
		isTaskFeedbackActive: false,
		isTaskDependenciesActive: false,
		isProjectWidgetActive: false,
		latestBaselineId: 0,
		lowestCommunityId: 0,
		baselineIdsFilter: [],
	});


	const updateBaselineId = (newBaselineId: number) => {
		setBaselineId(newBaselineId);
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setFormFilters((prevState) => ({
			...prevState,
			latestBaselineId: newBaselineId,
		}));
	};

	const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

	//#TODO Fin right subId and BaselineId
	const getFormFilters = async () => dispatch(newProjectWizardActions.loadFormFilters(subscriptionId, baselineId));
	const saveBaselineTaskNewWizard = async (task: object) => dispatch(newProjectWizardActions.saveBaselineTask(task));

	const createTaskModal = useRef();

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		// Update currentFormState whenever baselineId changes
		setCurrentFormState({ ...currentFormState, baselineId: baselineId });
		setFormData({ ...formData, baselineId: baselineId });
	}, [baselineId]);

	useEffect(() => {
		// Sync currentFormState when formData changes, ensuring it is up-to-date
		setCurrentFormState((prevState) => ({
			...prevState,
			editTask: formData,
			unchangedTask: formData,
			newTask: formData,
		}));
	}, [formData]);

	const init = async () => {
		const suppressTutorial = localStorage.getItem('suppressNewProjectWizardTaskListTutorial');
		setShowTutorial(suppressTutorial === 'false' ? true : false);
	};

	const loadFormFilters = async () => {
		setIsLoading(true);
		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		try {
			const result = await getFormFilters();
			const payload = result.payload;
			if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_SUCCESS) {
				setFormFilters(payload);
				//We need a baseline before creating a task so we set it as the latest plus one.
				if (payload.latestBaselineId > 0) {
					const newBaselineId = baselineId === 0 ? payload.latestBaselineId + 1 : baselineId;

					if (baselineId === 0) {
						setBaselineId(newBaselineId);
						updateBaselineId(newBaselineId);
					}

					const updatedState = {
						...currentFormState,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					const updatedFormDataState = {
						...formData,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					setCurrentFormState(updatedState);
					setFormData(updatedFormDataState);
				}
			} else if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setCurrentFormState({
				...currentFormState,
				isLoading: false,
			});
			setIsLoading(false);
		}
	};

	const onFormChange = (name, value) => {
		setFormData((prevData) => {
			const updatedFormData = { ...prevData, [name]: value };

			// Update currentFormState directly within this callback to ensure it has the latest formData
			setCurrentFormState((prevState) => ({
				...prevState,
				editTask: updatedFormData,
				unchangedTask: updatedFormData,
				newTask: updatedFormData,
			}));

			return updatedFormData;
		});
	};

	const onSelectImportType = (templateType: string) => {
		setImportType(templateType);
		setWizardSubStep('c.1');
	};

	const validateForm = (task) => {
		const { name, jobTitleId, departmentId, communityId } = task;

		// if there are steps, make sure they all have a name
		for (let i = 0; i < task.steps?.length; i++) {
			if (task.steps[i].name == '') {
				return false;
			}
		}

		for (let i = 0; i < task.links?.length; i++) {
			const link = task.links[i];
			const hasUrl = (link.url ?? '').trim().length > 0;
			const hasName = (link.name ?? '').trim().length > 0;
			if (hasUrl && !hasName) {
				// if there are links, make sure they all have abbreviations
				// if there is white space clear it out so element validation is visible
				link.name = '';
				const links = task.links.slice();
				links.splice(i, 1, link);
				//#TODO FIX THIS
				onFormChange('links', links);
				return false;
			}
			if (hasName && !hasUrl) {
				Notification('error', 'Link URL Required', `The link "${(link.name ?? '').trim()}" is missing a URL. Add a URL or remove the link.`);
				return false;
			}
		}

		if (currentFormState.editTask?.showStaticDates && !currentFormState.editTask.targetStaticDate) {
			return false;
		}

		if (
			currentFormState.editTask?.showStaticDates === false &&
			(currentFormState.editTask.targetWeeks === null || currentFormState.editTask.targetWeeks === undefined)
		) {
			return false;
		}

		// Validate that at least one of targetWeeks or targetDays is non-zero
		// Validate that targetDays is provided when using relative dates
		if (
			currentFormState.editTask?.showStaticDates === false &&
			(currentFormState.editTask.targetDays === null || currentFormState.editTask.targetDays === undefined)
		) {
			return false;
		}

		if (name && name.trim().length > 0 && jobTitleId && departmentId && communityId) {
			return true;
		}

		return false;
	};

	const onSave = async (saveDataAndRetention) => {
		if (currentFormState.isLoading) {
			return;
		}

		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		const task = {
			...currentFormState.editTask,
			baselineId: currentFormState.editTask?.baselineId || baselineId,
			projectKey: projectKey,
		};

		if (saveDataAndRetention) {
			setCurrentFormState({
				...currentFormState,
				saveAndNewRetention: !currentFormState.saveAndNewRetention,
				showDateError: false,
			});
		}

		if (!validateForm(task)) {
			setCurrentFormState((prevState) => ({
				...prevState,
				isFormValid: false,
				isLoading: false,
			}));
			return;
		}

		if (currentFormState.editTask?.showStaticDates) {
			// clear out any values in the other section that isn't visible so it does transport to api
			task.targetDaysOut = null;
			task.startDaysOut = null;
			task.targetDays = null;
			task.targetWeeks = null;
			task.startDays = null;
			task.startWeeks = null;
			task.isLocationTaskDateAdjustable = false;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive) {
				if (task.startStaticDate !== undefined || task.targetStaticDate !== undefined) {
					const start = task.startStaticDate ? new Date(task.startStaticDate) : null;
					const target = task.targetStaticDate ? new Date(task.targetStaticDate) : null;

					// NOTE: you can start and finish something the same day
					if (start && target && target.getTime() < start.getTime()) {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: true,
							isFormValid: false,
							isLoading: false,
						}));
						return;
					} else {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: false,
							isFormValid: false,
							isLoading: false,
						}));
					}
				}
			}
		} else {
			// days and weeks
			const targetWeeks = task.targetWeeks === null ? 0 : parseInt((task.targetWeeks ?? 0).toString());
			const targetDays = task.targetDays === null ? 0 : parseInt((task.targetDays ?? 0).toString());
			const startWeeks = task.startWeeks === null ? 0 : parseInt((task.startWeeks ?? 0).toString());
			const startDays = task.startDays === null ? 0 : parseInt((task.startDays ?? 0).toString());

			// convert target/start dates to total days (values can be negative!)
			task.targetDaysOut = targetWeeks * 7 + targetDays;
			task.startDaysOut = startWeeks * 7 + startDays;

			task.startStaticDate = null;
			task.targetStaticDate = null;

			// Relative-date tasks must send a non-null adjustable flag (server rejects null).
			// Default mirrors BaselineTaskDrawer: locked when the project widget is active.
			task.isLocationTaskDateAdjustable = formFilters.isProjectWidgetActive ? false : true;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive && task.startDaysOut > 0) {
				if (task.targetDaysOut > task.startDaysOut) {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: true,
						isFormValid: false,
						isLoading: false,
					}));
					return;
				} else {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: false,
						isFormValid: false,
						isLoading: false,
					}));
				}
			} else {
				task.startDaysOut = null;
			}
		}

		// if IsActive make sure isLocationTaskListActive is true
		if (task.isActive) {
			task.isLocationTaskListActive = true;
		}

		// Remove only fully blank placeholder rows. A link with either field filled is kept:
		// the server treats a missing link as "deleted" and deactivates it everywhere, so
		// partially-filled links must be blocked by validateForm, never silently dropped here.
		if (task.links && task.links.length) {
			task.links = task.links.filter((x) =>
				(x.name && x.name.trim().length) || (x.url && x.url.trim().length));
		}

		//transforms taskTagIds from string to array of strings
		if (task.taskTagIds && task.taskTagIds.length > 0) {
			if (!Array.isArray(task.taskTagIds)) {
				task.taskTagIds = String(task.taskTagIds).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
			} else {
				task.taskTagIds = task.taskTagIds.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
			}
		}

		setCurrentFormState((prevState) => ({
			...prevState,
			isFormValid: true,
			isLoading: true,
		}));

		try {
			if (typeof (task as any).baselineIdsSelected === 'string' && (task as any).baselineIdsSelected.length > 0) {
				const stringArray = String((task as any).baselineIdsSelected)
					.split(',')
					.map((id) => Number(id.trim()))
					.filter((id) => !isNaN(id));
				(task as any).baselineIdsSelected = stringArray;
			} else if (!Array.isArray((task as any).baselineIdsSelected)) {
				(task as any).baselineIdsSelected = [];
			}

			const response = await saveBaselineTaskNewWizard(task);
			if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_SUCCESS) {
				Notification(
					'success',
					'Success',
					'Baseline Task Created'
				);
				const currentBaselineId = currentFormState.editTask?.baselineId;
				if (saveDataAndRetention) {
					// retain these properties and keep the drawer open
					const retainProps = {
						isMilestone: currentFormState.editTask?.isMilestone,
						jobTitleId: currentFormState.editTask?.jobTitleId,
						departmentId: currentFormState.editTask?.departmentId,
						communityId: currentFormState.editTask?.communityId,
						phaseId: currentFormState.editTask?.phaseId,
						baselineId: currentBaselineId,
						isActive: currentFormState.editTask?.isActive,
						baselineIdsSelected: currentFormState.editTask?.baselineIdsSelected,
					};

					const newTask = {
						...defaultFormData,
						...retainProps,
					};

					if (response.payload.baselineId && response.payload.baselineId !== 0) updateBaselineId(response.payload.baselineId);

					onDrawerComplete(currentBaselineId);

					const mappedNewTask: Task = {
						id: newTask.id || 0,
						name: newTask.name || '',
						description: newTask.description || '',
						contingentId: newTask.contingentId || 0,
						phaseId: newTask.phaseId || '',
						departmentId: newTask.departmentId || '',
						communityId: newTask.communityId || '',
						jobTitleId: newTask.jobTitleId || '',
						contingentName: newTask.contingentName || '',
						baselineId: newTask.baselineId || 0,
						contactName: newTask.contactName || '',
						contactEmail: newTask.contactEmail || '',
						additionalInfo: newTask.additionalInfo || '',
						taskTagIds: Array.isArray(newTask.taskTagIds) ? newTask.taskTagIds : [],
						isActive: newTask.isActive ?? true,
						isLocationTaskListActive: newTask.isLocationTaskListActive ?? true,
						isLocationTaskDateAdjustable: newTask.isLocationTaskDateAdjustable ?? true,
						links: Array.isArray(newTask.links) ? newTask.links : [],
						targetDaysOut: newTask.targetDaysOut ?? null,
						targetDays: newTask.targetDays ?? null,
						targetWeeks: newTask.targetWeeks ?? null,
						targetStaticDate: newTask.targetStaticDate ?? null,
						startDaysOut: newTask.startDaysOut ?? null,
						startDays: newTask.startDays ?? null,
						startWeeks: newTask.startWeeks ?? null,
						startStaticDate: newTask.startStaticDate ?? null,
						anticipatedEffort: newTask.anticipatedEffort || '',
						steps: Array.isArray(newTask.steps) ? newTask.steps : [],
						prereqTasks: Array.isArray(newTask.prereqTasks) ? newTask.prereqTasks : [],
						showStaticDates: newTask.showStaticDates ?? false,
						createdByName: newTask.createdByName || '',
						createdDate: newTask.createdDate || '',
						modifiedByName: newTask.modifiedByName || '',
						modifiedDate: newTask.modifiedDate || '',
						isMilestone: newTask.isMilestone ?? false,
						includeCertificationCheckbox: newTask.includeCertificationCheckbox ?? false,
						baselineIdsSelected: Array.isArray(newTask.baselineIdsSelected) ? newTask.baselineIdsSelected : []
					};

					setFormData(mappedNewTask);

					setCurrentFormState((prevState) => ({
						...prevState,
						editTask: mappedNewTask,
						isLoading: false,
						saveAndNewRetention: false,
						baselineId: baselineId,
						showDateError: false,
					}));
				} else {
					//We assign this to a new baseline. wILL BE LOADED IN FORM FILTERS
					const newBaselineId = 0;
					onDrawerComplete();
					setCurrentFormState({
						...currentFormState,
						editTask: formData,
						unchangedTask: formData,
						newTask: formData,
					});
					updateBaselineId(newBaselineId);
					nextStep(currentBaselineId);
				}
			} else if (
				response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_FAILURE
			) {
				Notification('error', 'An Error Occurred', response.payload.message);
				setCurrentFormState((prevState) => ({
					...prevState,
					isLoading: false,
				}));
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));
		} finally {
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));
		}
	};

	const resetFormData = (baselineId) => {
		setFormData({
			...defaultFormData,
			baselineId: baselineId,
		});
	};

	const onDrawerComplete = (baselineId = 0) => {
		if (baselineId === 0) {
			setShowCreateTask(false);
		}
		resetFormData(baselineId);

		setCurrentFormState({
			...currentFormState,
			editTask: formData,
			unchangedTask: formData,
			newTask: formData,
		});
		//createTaskModal.current.hide()
	};

	const checkForChanges = () => {
		// compare unChangedTask to editTask if editing a task
		if (currentFormState.editTask?.id != 0) {
			const haveTheSameValues = _.isEqual(currentFormState.unchangedTask, currentFormState.editTask);
			setCurrentFormState((prevState) => ({
				...prevState,
				unsavedChanges: !haveTheSameValues,
			}));
		}
	};

	const openTaskModalHandler = async () => {
		await loadFormFilters();
		setShowCreateTask(true);
	};

	return (
		<div className='add-review-tasks-content'>
			<div className='wizard-content-title'>How would you like to add tasks to your new project?</div>

			<div className='what-is-link'>
				<button type='button' className='btn btn-link' onClick={() => setShowTutorial(true)}>
					<i className='fa-regular fa-info-circle fa-lg'></i>
					&nbsp;&nbsp;Learn how Pacer organizes tasks.
				</button>
			</div>

			<div className='container'>
				<div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4'>
					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={() => setWizardSubStep('b.1')}>
							<div className='card-body'>
								<i className='fa-regular fa-table-list fa-2x'></i>
								<h5 className='card-title'>Copy from an Existing Project</h5>
								<p className='card-text'>Copy tasks from a preexisting project to your new project.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={() => setShowImportOptions(true)}>
							<div className='card-body'>
								<i className='fa-regular fa-file-import fa-2x'></i>
								<h5 className='card-title'>Import New Tasks From Excel</h5>
								<p className='card-text'>Use our import tool to create new tasks in bulk.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center clicky-card' onClick={openTaskModalHandler}>
							<div className='card-body'>
								<i className='fa-regular fa-circle-plus fa-2x'></i>
								<h5 className='card-title'>Create New Tasks in Pacer</h5>
								<p className='card-text'>You will create tasks in the next step.</p>
							</div>
						</div>
					</div>

					<div className='col mb-4'>
						<div className='card text-center coming-soon-card'>
							<div className='card-body'>
								<i className='fa-regular fa-list-dropdown fa-2x'></i>
								<h5 className='card-title'>Select from a Template</h5>
								<p className='card-text'>Choose from our library of common Task Lists to get started</p>
							</div>

							<span className='badge pacer61-badge'>
								<i className='fa-regular fa-sparkles'></i>Coming Soon!
							</span>
						</div>
					</div>
				</div>

				{showImportOptions && (
					<ImportOptions onSelectImportType={onSelectImportType} />
				)}
			</div>

			{showTutorial && <TutorialModal setShowTutorial={setShowTutorial} />}
			{showCreateTask && (
				<CreateTaskForm
					taskFormMode={'create'}
					saveClick={onSave}
					cancelClick={() => {
						setShowCreateTask(false);
					}}
					currentTask={formData}
					isFormValid={currentFormState.isFormValid}
					onFormChange={onFormChange}
					checkForChanges={checkForChanges}
					formFilters={formFilters}
					baselineId={baselineId}
					showDateError={currentFormState.showDateError}
					isFromProject={true}
				/>
			)}
		</div>
	);
};

export default AddReviewTasks;
